import { Menu, Transition } from '@headlessui/react';
import AppsIcon from '@mui/icons-material/Apps';
import InventoryIcon from '@mui/icons-material/Inventory';
import PieChartIcon from '@mui/icons-material/PieChart';
import PollIcon from '@mui/icons-material/Poll';
import SettingsIcon from '@mui/icons-material/Settings';
import { IconButton } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'store/store';
import classNames from 'classnames';
import { contextActions } from 'store';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ROLE_SUPER_ADMIN,
  ROLE_TENANT_ADMIN,
  ROLE_TENANT_TECHNICIAN
} from 'shared/permissions/roles';
import Breadcrumbs from './Breadcrumbs';
import NotificationsDropdown from './NotificationsDropdown';
import UserMenuDropdown from './UserMenuDropdown';
import WarehouseSelector from './WarehouseSelector';

type ContextRef = 'LAB' | 'EVALUATE' | 'REFERTING' | 'SETTINGS';

interface ContextItem {
  ref: ContextRef;
  icon: React.ComponentType<any>;
  color: string;
  requiredRoles?: string[];
}

const contexts: ContextItem[] = [
  { ref: 'LAB', icon: InventoryIcon, color: '#29456c' },
  {
    ref: 'EVALUATE',
    icon: PieChartIcon,
    color: '#0163ae',
    requiredRoles: [ROLE_SUPER_ADMIN]
  },
  {
    ref: 'REFERTING',
    icon: PollIcon,
    color: '#0791e7',
    requiredRoles: [ROLE_SUPER_ADMIN, ROLE_TENANT_ADMIN, ROLE_TENANT_TECHNICIAN]
  },
  { ref: 'SETTINGS', icon: SettingsIcon, color: '#64b4fe', requiredRoles: [ROLE_SUPER_ADMIN] }
];

const TopNavbar = () => {
  const groups = useAppSelector((state) => state.auth.groups);

  const themeColor = groups.includes(ROLE_SUPER_ADMIN) ? 'stone' : 'primary';

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [context, setContext] = useState<ContextRef>('LAB');

  const visibleContexts = useMemo(() => {
    return contexts.filter((ctx) => {
      // Se non ci sono ruoli richiesti, è visibile a tutti
      if (!ctx.requiredRoles || ctx.requiredRoles.length === 0) return true;

      // Il Super Admin bypassa tutti i controlli e vede tutto
      if (groups.includes(ROLE_SUPER_ADMIN)) return true;

      // Controlla se l'utente ha almeno uno dei ruoli richiesti
      return ctx.requiredRoles.some((role) => groups.includes(role));
    });
  }, [groups]);

  const switchContext = (context: ContextRef) => {
    dispatch(contextActions.setContext(context));
    setContext(context);
  };

  return (
    <div className="w-full bg-[#f8f9fa] border-b border-slate-200/60 sticky top-0 z-30 select-none">
      <div className="flex justify-between items-center h-14 px-6">
        {/* PARTE SINISTRA: Menu a Griglia (Contesti) + Breadcrumbs */}
        <div className="flex items-center gap-4 flex-1 min-w-0">
          {/* Menu Dropdown della Griglia */}
          <Menu as="div" className="relative inline-block text-left z-50">
            <div>
              <Menu.Button as={Fragment}>
                <IconButton
                  size="small"
                  className="text-slate-500 hover:bg-slate-200/50 rounded-lg p-1.5 transition-colors focus:outline-none">
                  <AppsIcon fontSize="medium" sx={{ color: '#5a6268' }} />
                </IconButton>
              </Menu.Button>
            </div>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95">
              {/* Pannello Dropdown  */}
              <Menu.Items className="absolute left-0 mt-2 w-56 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 divide-y divide-slate-100/60 p-0.5 border border-slate-200/40">
                {visibleContexts.map((ctx) => {
                  const isCurrent = context === ctx.ref;
                  const label = t(`context.${ctx.ref}`);

                  return (
                    <Menu.Item key={ctx.ref}>
                      {({ active }) => (
                        <button
                          onClick={() => switchContext(ctx.ref)}
                          className={classNames(
                            active || isCurrent ? 'bg-slate-50 text-[#1e3a8a]' : 'text-[#212529]',
                            'group flex items-center w-full px-4 py-3 text-[14px] font-medium transition-colors text-left rounded-sm'
                          )}>
                          {/* Icona dinamica del contesto */}
                          <ctx.icon
                            className={classNames(
                              isCurrent
                                ? 'text-[#1e3a8a]'
                                : 'text-slate-500 group-hover:text-slate-700',
                              'mr-4 h-5 w-5 flex-shrink-0'
                            )}
                            sx={{ fontSize: '20px' }}
                          />
                          <span>{label}</span>
                        </button>
                      )}
                    </Menu.Item>
                  );
                })}
              </Menu.Items>
            </Transition>
          </Menu>

          {/* Breadcrumbs di navigazione */}
          <div className="pt-0.5">
            <Breadcrumbs />
          </div>
        </div>

        {/* Selettore Magazzino, Separatore, Notifiche e Profilo */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="min-w-[160px]">
            <WarehouseSelector />
          </div>

          {/* Divisore Verticale Sottile */}
          <div className="h-5 w-[1px] bg-slate-200 mx-1"></div>

          <NotificationsDropdown />

          <UserMenuDropdown />
        </div>
      </div>
    </div>
  );
};

export default TopNavbar;
